import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Minus, Plus, ShoppingCart, Tag } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { getCatalogPromoTypeLabel, promoCatalogItemToCartProduct } from '@/lib/menu/catalogPromos';
import { translateMenuTerm } from '@/i18n/translateMenuContent';
import {
  getMenuMutedTextStyle,
  getMenuPrimaryButtonStyle,
  getPremiumModalIconButtonClass,
} from '@/lib/menuTheme';

export default function CatalogPromoModal({
  open,
  onOpenChange,
  promoItem,
  bizForTheme,
  standard,
  premium,
  pl,
  branding: b,
  accent,
  tc,
  isFallbackMode,
  businessId,
  addToCart,
  onAdded,
}) {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const [quantity, setQuantity] = useState(1);

  const handleOpenChange = (next) => {
    if (!next) setQuantity(1);
    onOpenChange(next);
  };

  const handleAddToCart = () => {
    if (!promoItem || isFallbackMode) return;
    const product = promoCatalogItemToCartProduct(promoItem);
    addToCart(product, [], [], quantity, { businessId });
    handleOpenChange(false);
    onAdded?.(product);
  };

  if (!promoItem) return null;

  const linked = promoItem.catalog_promo_products || [];
  const typeLabel = getCatalogPromoTypeLabel(promoItem.catalog_promo_type);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className={cn('max-w-2xl max-h-[90vh] overflow-y-auto', standard && tc?.modalSurface)}
        style={
          premium
            ? {
                backgroundColor: b.card_background_color,
                color: b.text_color,
                borderColor: b.card_border_color,
              }
            : undefined
        }
      >
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2 pr-8">
            {translateMenuTerm(promoItem.name, lng)}
            <Badge className="bg-violet-600 text-white">{typeLabel}</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {promoItem.photo_url ? (
            <div className="w-full h-56 rounded-xl overflow-hidden">
              <img
                src={promoItem.photo_url}
                alt={promoItem.name}
                className="w-full h-full object-cover"
              />
            </div>
          ) : null}

          {promoItem.description ? (
            <p
              className={cn('text-sm leading-relaxed', standard && tc?.productMuted)}
              style={premium ? getMenuMutedTextStyle(bizForTheme) : undefined}
            >
              {translateMenuTerm(promoItem.description, lng)}
            </p>
          ) : null}

          {linked.length > 0 ? (
            <div className="rounded-lg border p-3 space-y-2">
              <p className="text-sm font-medium flex items-center gap-2">
                <Tag className="w-4 h-4" />
                {t('catalog.comesWith').replace(/:?\s*$/, '')}
              </p>
              <ul className="text-sm space-y-1">
                {linked.map((p) => (
                  <li key={p.id} className="flex justify-between gap-2">
                    <span>{translateMenuTerm(p.name, lng)}</span>
                    <span className="text-muted-foreground shrink-0">
                      ${(Number(p.final_price ?? p.price) || 0).toLocaleString('es-CL')}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-3">
            {promoItem.compare_at_price ? (
              <span
                className={cn('text-sm line-through', standard && tc?.productMuted)}
                style={premium ? getMenuMutedTextStyle(bizForTheme) : undefined}
              >
                ${promoItem.compare_at_price.toLocaleString('es-CL')}
              </span>
            ) : null}
            <span className="font-bold text-2xl" style={{ color: accent }}>
              ${(Number(promoItem.final_price) || 0).toLocaleString('es-CL')}
            </span>
          </div>

          <div className="flex items-center justify-between gap-4 pt-2">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="icon"
                className={premium ? getPremiumModalIconButtonClass(bizForTheme, pl) : undefined}
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                aria-label="Menos"
              >
                <Minus className="w-4 h-4" />
              </Button>
              <span className="w-8 text-center font-semibold">{quantity}</span>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className={premium ? getPremiumModalIconButtonClass(bizForTheme, pl) : undefined}
                onClick={() => setQuantity((q) => Math.min(99, q + 1))}
                aria-label="Más"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            <Button
              className="gap-2 flex-1 sm:flex-none"
              style={premium ? getMenuPrimaryButtonStyle(bizForTheme) : undefined}
              onClick={handleAddToCart}
              disabled={isFallbackMode}
              data-testid="catalog-promo-add-to-cart"
            >
              <ShoppingCart className="w-4 h-4" />
              Agregar promo
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
